import { IDeposit, IDepositUnmatchedItem } from './../../models/deposit';
import { Injectable, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IInvoiceResult } from '../../models/invoice-result';
import { ConfigSettings } from '../../models/settings';
import { ITransaction } from '../../models/transaction';
import { OrderDetails } from '../../shared/models/order';
import { DataService } from '../data-services/data.service';
import { TransactionImportResult } from './transaction-import-result';
import { IOrganizationSubscription } from '../../models/organization-subscription';
import { Pager } from '../../models/pager';
import { SubscriptionProduct } from '../../models/subscription-product';
import { ConvenienceFeeService } from '../convenience-fee/convenience-fee.service';

//Keep Track of account balance / updating balances
// http://learnmongodbthehardway.com/schema/transactions/

// initial
// pending
// committed

// add pending transactions

@Injectable({
  providedIn: 'root',
})
export class TransactionService
  extends DataService<ITransaction>
  implements OnInit
{
  getSplitTransactions(
    primaryTransactionId: string
  ): Observable<ITransaction[]> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      primaryTransactionId
    )}/split-transactions`;
    return this.http.get<any>(`${url}`);
  }
  updateTransactionLabels(id: any, selectedTransactions: ITransaction[]) {
    const url = this.urlService.buildUrl(
      this.collectionname,
      id,
      'assign-label'
    );
    const transactionIds = selectedTransactions.map(
      (trax) => trax.id || trax['_id']
    );
    let data = {
      transactionIds: transactionIds,
      labelId: id,
    };
    return this.http
      .patch<any>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  updateBudgetItems(budgetId: string, selectedTransactions: ITransaction[]) {
    const url = this.urlService.buildUrl(
      this.collectionname,
      budgetId,
      'assign-budget'
    );
    const transactionIds = selectedTransactions.map((trax) => trax.id);
    let data = {
      transactionIds: transactionIds,
      budgetId: budgetId,
    };
    return this.http
      .patch<any>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  setBulkBudgetDate(budgetDate: Date, selectedTransactions: ITransaction[]) {
    const url = this.urlService.buildUrl(
      this.collectionname,
      'bulk',
      'set-budget-date'
    );
    const transactionIds = selectedTransactions.map((trax) => trax.id);
    return this.http
      .patch<any>(url, { transactionIds, budgetDate }, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }
  sendReceipt(transactionId: string, memberId: string): Observable<any> {
    const url = this.urlService.buildUrl(
      this.collectionname,
      transactionId,
      'send-receipt'
    );

    let body = {
      transactionId: transactionId,
      memberId: memberId,
    };

    return this.http.post<any>(`${url}`, body);
  }
  assignExpense(transactionId: string, memberId: string): Observable<any> {
    const url = this.urlService.buildUrl(
      this.collectionname,
      transactionId,
      'assign-expense'
    );

    let body = {
      transactionId: transactionId,
      memberId: memberId,
    };

    return this.http.post<any>(`${url}`, body);
  }

  payInvoice(transaction: ITransaction): Observable<any> {
    //const { organizationId } = transaction;
    let invoiceId = transaction.id;
    let organizationId =
      transaction.organizationId && transaction.organizationId['_id']
        ? transaction.organizationId['_id']
        : transaction.organizationId;

    const url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/invoice-id/${invoiceId}/pay`;

    return this.http.post(`${url}`, transaction).pipe(
      map((trax: any) => {
        return trax || ({} as any);
      }),
      catchError(this.errorMgmt)
    );
  }
  createSubscription(
    transaction: ITransaction,
    selectedCard: any
  ): Observable<any> {
    const url = `${this.urlService.buildUrl('pay', 'addSubscription')}`;

    let body = {
      transaction,
      card: selectedCard,
    };
    console.log('url', url);
    return this.http.post<any>(`${url}`, body);
  }

  cancelSubscription(subscription: IOrganizationSubscription): Observable<any> {
    const url = `${this.urlService.buildUrl(
      'pay',
      subscription.id
    )}/cancel-subscription`;

    let body = {
      subscription,
    };
    console.log('url', url);
    return this.http.post<any>(`${url}`, body);
  }

  getInvoicePaymentLink(organizationId: string): Observable<any> {
    const url = `${this.urlService.buildUrl('account', organizationId)}/activate-payments`;
    return this.http.get<any>(url, { headers: this.headers });
  }

  getDepositPaymentLink(ipAddress: string = ''): Observable<any> {
    const url = `${this.urlService.buildUrl(
      'account',
      this.user.id
    )}/activate-deposits?ip=${ipAddress}`;

    // let body = {
    //   organizationId: this.organization.id,
    //   ipAddress: ipAddress,
    // };
    console.log('url', url);
    return this.http.get<any>(`${url}`);
  }

  getTransferStatus(): Observable<any> {
    const url = `${this.urlService.buildUrl(
      'account',
      this.user.id
    )}/account-status`;

    console.log('url', url);
    return this.http.get<any>(`${url}`);
  }

  protected collectionname = 'Transactions';
  protected _relativeUrl = 'Transactions';

  selectedTransaction: ITransaction;

  ngOnInit(): void {
    this.setCollectionName('Transactions');
  }

  getByUserid(userId: string, organizationId): Observable<Array<ITransaction>> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'findByUser'
    )}/${organizationId}/user/${userId}`;
    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  cancel(id: any, data: ITransaction): Observable<ITransaction> {
    const url = `${this.baseUri}/${this.collectionname}/cancel/${id}`;
    return this.http
      .patch<ITransaction>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  deleteOrder(id: any, data: ITransaction): Observable<ITransaction> {
    const url = `${this.baseUri}/${this.collectionname}/delete/${id}`;

    const updateBody = {
      status: data.status,
      createdBy: this.user.id,
      id,
    };

    return this.http
      .patch<ITransaction>(url, updateBody, { headers: this.headers })
      .pipe(
        map((res: ITransaction) => {
          this.setId(res);
          return res || ({} as ITransaction);
        }),
        catchError(this.errorMgmt)
      );
  }

  reverse(id: any, data: ITransaction): Observable<ITransaction> {
    const url = `${this.baseUri}/${this.collectionname}/reverse/${id}`;
    return this.http
      .patch<ITransaction>(url, data, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  getInvoice(
    invoiceId: string,
    organizationId: string
  ): Observable<IInvoiceResult> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/invoice-id/${invoiceId}/pay`;
    return this.http.get<IInvoiceResult>(`${url}`).pipe(
      map((trax: IInvoiceResult) => {
        // res.forEach((item) => {
        this.setId(trax.invoice);
        this.setId(trax.user);
        // });
        return trax || ({} as IInvoiceResult);
      }),
      catchError(this.errorMgmt)
    );
  }

  getUnassigned(
    organizationId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<Array<ITransaction>> {
    let url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/unassigned`;

    // Add date range query parameters if provided
    const params: string[] = [];
    if (startDate) {
      params.push(`startDate=${startDate.toISOString()}`);
    }
    if (endDate) {
      params.push(`endDate=${endDate.toISOString()}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  excludeFromBudget(transactionId: string, exclude: boolean): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      transactionId
    )}/exclude-from-budget`;
    return this.http
      .patch<any>(url, { exclude }, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  getByOrganizationId(
    organizationId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<Array<ITransaction>> {
    let url = `${this.urlService.buildUrl(
      this.collectionname,
      'findByOrganization'
    )}/${organizationId}`;

    // Add date range query params if provided
    if (startDate && endDate) {
      const params = new URLSearchParams();
      params.set('startDate', startDate.toISOString());
      params.set('endDate', endDate.toISOString());
      url += `?${params.toString()}`;
    }

    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getByPage(
    organizationId,
    pager: Pager
  ): Observable<{ results: Array<ITransaction>; number: number }> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/find-by-page`;
    return this.http
      .post<{ results: Array<ITransaction>; number: number }>(`${url}`, pager)
      .pipe(
        map((res: { results: Array<ITransaction>; number: number }) => {
          res.results.forEach((item) => {
            this.setId(item);
          });
          return (
            res || ({} as { results: Array<ITransaction>; number: number })
          );
        }),
        catchError(this.errorMgmt)
      );
  }

  getCards(customerId: string): Observable<Array<any>> {
    const url = `${this.urlService.buildUrl('pay', 'cards')}/${customerId}`;
    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<any>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<any>);
      }),
      catchError(this.errorMgmt)
    );
  }

  addCard(customerId: string, token: string): Observable<any> {
    const url = `${this.urlService.buildUrl('pay', 'addSource')}`;

    let body = {
      customerId,
      token,
    };
    console.log('url', url);
    return this.http.post<any>(`${url}`, body);
  }

  deleteCard(customerId: string, cardId: any): Observable<any> {
    const url = `${this.urlService.buildUrl('pay', 'deleteSource')}`;

    let body = {
      customerId,
      cardId,
    };
    console.log('url', url);
    return this.http.post<any>(`${url}`, body);
  }

  getInvoicesByOrganizationId(organizationId: string) {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'findInvoicesByOrganization'
    )}/${organizationId}`;
    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getApprovalsByOrganizationId(
    organizationId: string
  ): Observable<Array<ITransaction>> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'findApprovalsByOrganization'
    )}/${organizationId}`;
    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getApprovals(
    organizationId: string,
    userId: string
  ): Observable<Array<ITransaction>> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'findApprovals'
    )}/${organizationId}/user/${userId}`;

    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  getCompletions(
    organizationId: string,
    userId: string
  ): Observable<Array<ITransaction>> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'pending-completions'
    )}/${organizationId}`;

    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  updateApprovals(
    transactionId: string,
    approvalId: string,
    userId: string,
    status: string
  ): Observable<ITransaction> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'approval'
    )}/${approvalId}/user/${userId}`;

    let body = {
      id: approvalId,
      transactionId,
      userId,
      status,
    };
    return this.http.post<ITransaction>(`${url}`, body);
  }

  preTransaction(transactionId: string): Observable<any> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      'preTransact'
    )}/${transactionId}`;

    const intent = this.http.get<any>(`${url}`);
    return intent;
  }

  // Create  an enum for collectionNames
  async getPaymentSettings(): Promise<ConfigSettings> {
    const url = `${this.urlService.buildUrl('config')}`;
    const config = this.http.get<ConfigSettings>(url).toPromise();
    return config;
  }

  getPaymentSettingsObservable(): Observable<ConfigSettings> {
    const url = `${this.urlService.buildUrl('config')}`;
    return this.http.get<ConfigSettings>(url);
    //return from(config);
  }

  getAchPayments(organizationId: string): Observable<Array<ITransaction>> {
    const url = `${this.urlService.buildUrl(
      this.collectionname,
      organizationId
    )}/ach-payments`;

    return this.http.get<Array<ITransaction>>(`${url}`).pipe(
      map((res: Array<ITransaction>) => {
        res.forEach((item) => {
          this.setId(item);
        });
        return res || ({} as Array<ITransaction>);
      }),
      catchError(this.errorMgmt)
    );
  }

  async createPaymentIntentByItems(currency, items): Promise<any> {
    try {
      const url = `${this.urlService.buildUrl('pay', 'payment_intents')}`;

      const body = {
        currency,
        items,
      };

      const response = await this.http.post<any>(`${url}`, body).toPromise();

      const data = await response.json();
      if (data.error) {
        return { error: data.error };
      } else {
        return data;
      }
    } catch (err) {
      return { error: err.message };
    }
  }

  async createPaymentIntentByAmount(
    currency,
    amount,
    customerId
  ): Promise<any> {
    amount = Math.round(amount * 100);
    let accountId = '';

    let intent = {
      currency,
      amount,
    };

    if (this.organization && this.organization.organization) {
      accountId = this.organization.organization['accountId'];
      intent['accountId'] = accountId;
      intent['organizationId'] = this.organization.organizationId;
      intent['customerId'] = customerId;
    }

    try {
      const url = `${this.urlService.buildUrl('pay', 'payment_intents')}`;
      const response = await this.http.post<any>(`${url}`, intent).toPromise();

      const data = await response.json();
      if (data.error) {
        return { error: data.error };
      } else {
        return data;
      }
    } catch (err) {
      return { error: err.message };
    }
  }
  createIntent(order: OrderDetails) {
    const url = `${this.urlService.buildUrl('token', 'transaction-key')}`;
    const intent = {
      amount: order.amount,
      // token: order.token,
      // email: order.email,
      // firstname: order.firstname,
      // lastname: order.lastname,
      // address: order.address,
      // phone: order.phone,
      organizationId: order.organizationId,
    };
    return this.http.post<any>(`${url}`, order).toPromise();
  }

  createSubscriptionIntent(product: SubscriptionProduct): Observable<any> {
    const url = `${this.urlService.buildUrl(
      'pay',
      'subscription-transaction-key'
    )}`;
    const intent = {
      amount: product.amount,
      priceId: product.priceId,
      product: product,
      // token: order.token,
      // email: order.email,
      // firstname: order.firstname,
      // lastname: order.lastname,
      // address: order.address,
      // phone: order.phone,
      // organizationId: order.organizationId,
    };
    const order = {
      amount: product.amount,
      priceId: product['priceId'],
      product: product,
      // token: order.token,
      // email: order.email,
      // firstname: order.firstname,
      // lastname: order.lastname,
      // address: order.address,
      // phone: order.phone,
      // organizationId: order.organizationId,
    };
    return this.http.post<any>(`${url}`, order);
  }

  /**
   * Sets the payer's email / name on a PaymentIntent created before they were
   * entered (campaign checkout), right before it is confirmed.
   */
  updateTransactionIntentPayer(payer: {
    clientSecret: string;
    organizationId: string;
    email: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
  }): Observable<{ updated: boolean }> {
    const url = `${this.urlService.buildUrl('token', 'transaction-key')}/payer`;
    return this.http.post<{ updated: boolean }>(url, payer);
  }

  createTransactionIntent(order: OrderDetails): Observable<any> {
    const url = `${this.urlService.buildUrl('token', 'transaction-key')}`;
    const body = {
      amount: order.amount,
      email: order.email,
      firstname: order.firstname,
      lastname: order.lastname,
      organizationId: order.organizationId,
      transactionId: order.transactionId,
    };
    return this.http.post<any>(url, body);
  }

  confirmInvoicePayment(
    organizationId: string,
    invoiceId: string,
    paymentIntentId: string
  ): Observable<any> {
    const url = `${this.urlService.buildUrl(this.collectionname, organizationId)}/invoice-id/${invoiceId}/confirm-payment`;
    return this.http.post<any>(url, { paymentIntentId });
  }

  calculateConvenienceFee = (amount: number): number => {
    return ConvenienceFeeService.calculateConvenienceFee(amount);
  };

  import(records: ITransaction[]): Observable<TransactionImportResult> {
    const url = `${this.urlService.buildUrl('transactions', 'import')}`;
    let body = {
      importRecords: records,
    };
    return this.http.post<any>(`${url}`, body);
  }

  getDepositList(organizationId: string): Observable<IDeposit[]> {
    const url = `${this.urlService.buildUrl('deposits', organizationId)}/list`;
    return this.http.get<IDeposit[]>(`${url}`);
  }

  /** Stripe items in the deposit's payout that no duesPlus transaction accounts for. */
  getDepositUnmatchedItems(depositId: string): Observable<IDepositUnmatchedItem[]> {
    const url = `${this.urlService.buildUrl(
      'deposits',
      depositId
    )}/unmatched-items`;
    return this.http.get<IDepositUnmatchedItem[]>(`${url}`);
  }

  /** Records a Stripe payment from a deposit that duesPlus never saved. */
  recordDepositUnmatchedItem(
    depositId: string,
    balanceTransactionId: string,
    body: {
      campaignId?: string;
      productId?: string;
      quantity?: number;
      budgetItemId?: string;
      title?: string;
      note?: string;
    }
  ): Observable<{
    transaction: ITransaction;
    tickets: number;
    ticketError?: string;
    memberApplyStatus: string | null;
  }> {
    const url = `${this.urlService.buildUrl(
      'deposits',
      depositId
    )}/unmatched-items/${balanceTransactionId}/record`;
    return this.http.post<any>(url, body);
  }

  getDepositTransactions(depositId: string): Observable<ITransaction[]> {
    const url = `${this.urlService.buildUrl(
      'deposits',
      depositId
    )}/deposit-detail`;
    return this.http.get<ITransaction[]>(`${url}`);
  }
}
